import { getRelativeDocumentUrl } from "./stringHelper";
import { IPageSettings } from "../../interfaces/IPageSettings";

interface TeamsChannelRootFlags {
  isPrivate?: boolean;
  isShared?: boolean;
}

interface TeamsChannelDriveInfo {
  driveId?: string;
  driveItemId?: string;
  driveItemWebUrl?: string;
}

export const isTeamsChannelLibraryRootChannel = (channel?: TeamsChannelRootFlags): boolean =>
  channel?.isPrivate === true || channel?.isShared === true;

export const getTeamsChannelLocationParts = ({
  channel,
  driveInfo,
}: {
  channel?: TeamsChannelRootFlags;
  driveInfo?: TeamsChannelDriveInfo;
}): { id?: string; parentId?: string | null; directory: string } => {
  const isLibraryRoot = isTeamsChannelLibraryRootChannel(channel);

  return {
    id: isLibraryRoot ? driveInfo?.driveId : driveInfo?.driveItemId,
    parentId: isLibraryRoot ? null : driveInfo?.driveId,
    directory: getRelativeDocumentUrl(driveInfo?.driveItemWebUrl, isLibraryRoot),
  };
};

const normalizePath = (value?: string): string =>
  value?.trim().replace(/\\/g, "/").replace(/^\/+|\/+$/g, "").replace(/\/+/g, "/") ?? "";

const tryDecodePathSegment = (value?: string): string => {
  try {
    return decodeURIComponent(value ?? "");
  } catch (error) {
    return value ?? "";
  }
};

const samePathSegment = (first?: string, second?: string): boolean =>
  tryDecodePathSegment(first).toLowerCase() === tryDecodePathSegment(second).toLowerCase();

const getWebUrlPathSegments = (webUrl?: string): string[] => {
  try {
    return normalizePath(new URL(webUrl).pathname).split("/").filter(Boolean);
  } catch (error) {
    return [];
  }
};

const removeMatchingPrefix = (segments: string[], prefixSegments: string[]): string[] | undefined => {
  if (!prefixSegments.length || segments.length < prefixSegments.length) {
    return undefined;
  }

  const matchesPrefix = prefixSegments.every((segment, index) => samePathSegment(segment, segments[index]));
  return matchesPrefix ? segments.slice(prefixSegments.length) : undefined;
};

const normalizeTeamsChannelLibraryRootDirectory = (directory?: string, webUrl?: string): string => {
  const directorySegments = normalizePath(directory).split("/").filter(Boolean);
  const sitePathSegments = getWebUrlPathSegments(webUrl);

  if (!directorySegments.length || !sitePathSegments.length) {
    return normalizePath(directory);
  }

  const withoutSitePath = removeMatchingPrefix(directorySegments, sitePathSegments);
  if (withoutSitePath?.length) {
    return withoutSitePath.join("/");
  }

  const siteName = sitePathSegments[sitePathSegments.length - 1];
  if (samePathSegment(directorySegments[0], siteName)) {
    return directorySegments.slice(1).join("/");
  }

  return directorySegments.join("/");
};

const hasStaleTeamsChannelSiteDirectoryPrefix = (location?: IPageSettings): boolean => {
  if (location?.isTeamsRootFolder !== true || !location?.directory || !location?.webUrl) {
    return false;
  }

  const directorySegments = normalizePath(location.directory).split("/").filter(Boolean);
  const sitePathSegments = getWebUrlPathSegments(location.webUrl);
  const siteName = sitePathSegments[sitePathSegments.length - 1];

  return Boolean(
    directorySegments.length > 1
    && siteName
    && samePathSegment(directorySegments[0], siteName)
  );
};

export const isTeamsChannelLibraryRootLocation = (location?: IPageSettings): boolean =>
  location?.isTeamsRootFolder === true
  && (
    location?.isPrivateTeamsChannel === true
    || location?.isSharedTeamsChannel === true
    || hasStaleTeamsChannelSiteDirectoryPrefix(location)
  );

export const normalizeTeamsChannelLibraryRootLocation = (location?: IPageSettings): IPageSettings | undefined => {
  if (!isTeamsChannelLibraryRootLocation(location)) {
    return location;
  }

  return {
    ...location,
    id: location.parentId ?? location.id,
    parentId: null,
    directory: normalizeTeamsChannelLibraryRootDirectory(location.directory, location.webUrl),
    isPrivateTeamsChannel: location.isPrivateTeamsChannel ?? !location.isSharedTeamsChannel,
    sharePointUniqueId: undefined,
  };
};
